import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { AlertTriangle, TrendingUp, Fuel, Gauge } from "lucide-react";
import DateRangePicker, { useDateRange } from "@/components/DateRangePicker";

const INR = (n) => `₹${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [economy, setEconomy] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const range = useDateRange("30");

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/summary", { params: range.params });
      setSummary(data);
      if (!selectedVehicle && data.per_vehicle?.[0]) setSelectedVehicle(data.per_vehicle[0].id);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); /* eslint-disable-next-line */ }, [range.preset, range.from, range.to]);

  useEffect(() => {
    if (!selectedVehicle) return;
    (async () => {
      const { data } = await api.get(`/reports/economy/${selectedVehicle}`, { params: range.params });
      setEconomy(data);
    })();
  }, [selectedVehicle, range.preset, range.from, range.to]);

  if (loading && !summary) return <div className="text-[#737373]">Loading reports…</div>;

  const worst = summary?.worst_vehicle;
  const per = summary?.per_vehicle || [];
  const rangeTotal = summary?.range_total;
  const yearTotal = summary?.totals?.year;

  return (
    <div className="space-y-10">
      <div>
        <p className="ft-overline mb-2">ANALYTICS</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">Reports</h1>
        <p className="mt-3 text-[#A3A3A3]">Where the money goes, which vehicle bleeds most, and how mileage trends over time.</p>
      </div>

      {/* Date range control */}
      <DateRangePicker
        preset={range.preset}
        setPreset={range.setPreset}
        from={range.from}
        setFrom={range.setFrom}
        to={range.to}
        setTo={range.setTo}
        onRefresh={fetchSummary}
        testidPrefix=""
      />

      {/* Range totals + year */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <div className="ft-card p-6" data-testid="range-total-card">
          <p className="ft-overline">SELECTED RANGE</p>
          <p className="font-mono text-3xl font-medium mt-3 text-white">{INR(rangeTotal?.amount)}</p>
          <p className="mt-2 text-xs text-[#737373]">
            {rangeTotal ? `${rangeTotal.count} refuels · ${(rangeTotal.litres || 0).toFixed(1)} L` : "—"}
          </p>
        </div>
        <div className="ft-card p-6" data-testid="year-total-card">
          <p className="ft-overline text-[#FF5A1F]">THIS YEAR</p>
          <p className="font-mono text-3xl font-medium mt-3 text-[#FF5A1F]">{INR(yearTotal?.amount)}</p>
          <p className="mt-2 text-xs text-[#737373]">
            {yearTotal ? `${yearTotal.count} refuels · ${(yearTotal.litres || 0).toFixed(1)} L` : "—"}
          </p>
        </div>
        <div className="ft-card p-6">
          <p className="ft-overline">AVG DAILY SPEND</p>
          <p className="font-mono text-3xl font-medium mt-3 text-white">
            {rangeTotal && rangeTotal.count ? INR((rangeTotal.amount || 0) / Math.max(1, (summary?.daily_spend?.length || 1))) : "—"}
          </p>
          <p className="mt-2 text-xs text-[#737373]">Over {summary?.daily_spend?.length || 0} refuelling days</p>
        </div>
      </div>

      {/* Worst vehicle callout */}
      {worst ? (
        <div className="border border-[#EF4444]/40 bg-[#EF4444]/5 p-6 sm:p-8" data-testid="worst-vehicle-callout">
          <div className="flex items-start gap-4">
            <AlertTriangle size={28} className="text-[#EF4444] flex-shrink-0" />
            <div className="flex-1">
              <p className="ft-overline text-[#EF4444]">MOST POORLY RUN VEHICLE</p>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2">
                {worst.number} · <span className="text-[#A3A3A3]">{worst.name}</span>
              </h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Metric label="Avg mileage" value={`${worst.avg_mileage} km/l`} danger />
                <Metric label="Cost per km" value={`₹${worst.cost_per_km}`} />
                <Metric label="Total spent" value={INR(worst.total_amount)} />
                <Metric label="Refuels" value={worst.refuel_count} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-[#262626] bg-[#121212] p-6 text-[#A3A3A3]">
          Log at least 2 refuels per vehicle so we can rank them.
        </div>
      )}

      {/* Per-vehicle spend chart */}
      <div className="ft-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="ft-overline">SPEND BY VEHICLE</p>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Total ₹ per car</h3>
          </div>
          <Fuel size={20} className="text-[#FF5A1F]" />
        </div>
        <div className="h-72">
          {per.length ? (
            <ResponsiveContainer>
              <BarChart data={per} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="number" stroke="#737373" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#737373" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v}`} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", borderRadius: 0 }} formatter={(v) => INR(v)} />
                <Bar dataKey="total_amount" fill="#FF5A1F" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </div>
      </div>

      {/* Vehicle-wise table */}
      <div className="ft-card p-0 overflow-auto">
        <div className="p-6 pb-3">
          <p className="ft-overline">PER-VEHICLE BREAKDOWN</p>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Efficiency ranked</h3>
        </div>
        <table className="w-full" data-testid="per-vehicle-table">
          <thead>
            <tr>
              {["Vehicle", "Total ₹", "Litres", "KM Driven", "Avg km/l", "₹/km", "Refuels"].map((h) => (
                <th key={h} className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="font-mono text-sm">
            {per.map((p) => (
              <tr key={p.id} className={`border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors duration-150 cursor-pointer ${worst && p.id === worst.id ? "bg-[#EF4444]/5" : ""}`} onClick={() => navigate(`/vehicles/${p.id}`)} data-testid={`report-vehicle-row-${p.id}`}>
                <td className="py-4 px-4"><span className="font-bold text-white">{p.number}</span> <span className="text-[#A3A3A3] ml-2">{p.name}</span></td>
                <td className="py-4 px-4 text-white">{INR(p.total_amount)}</td>
                <td className="py-4 px-4 text-[#A3A3A3]">{p.total_litres.toFixed(1)} L</td>
                <td className="py-4 px-4 text-[#A3A3A3]">{p.total_km.toFixed(0)} km</td>
                <td className="py-4 px-4 text-[#FF5A1F]">{p.avg_mileage ?? "—"}</td>
                <td className="py-4 px-4">{p.cost_per_km ? `₹${p.cost_per_km}` : "—"}</td>
                <td className="py-4 px-4 text-[#A3A3A3]">{p.refuel_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Fuel economy trend */}
      <div className="ft-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="ft-overline">FUEL ECONOMY TREND</p>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">km/l over time</h3>
          </div>
          <select
            value={selectedVehicle}
            onChange={(e) => setSelectedVehicle(e.target.value)}
            className="bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200"
            data-testid="economy-vehicle-select"
          >
            {per.map((p) => (
              <option key={p.id} value={p.id}>{`${p.number} · ${p.name}`}</option>
            ))}
          </select>
        </div>

        {/* Period averages */}
        {economy?.averages && (
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="economy-averages">
            {[
              { key: "week", label: "1W AVG" },
              { key: "month", label: "1M AVG" },
              { key: "year", label: "1Y AVG" },
            ].map((w) => {
              const a = economy.averages[w.key];
              return (
                <div key={w.key} className="border border-[#262626] bg-[#0A0A0A] p-4" data-testid={`avg-${w.key}`}>
                  <p className="ft-overline text-[#737373]">{w.label}</p>
                  <p className="font-mono text-xl font-medium mt-2 text-[#FF5A1F]">
                    {a?.mileage != null ? `${a.mileage} km/l` : "—"}
                  </p>
                  <p className="text-xs text-[#737373] mt-1">
                    {a ? `${a.count} fills · ${a.km.toFixed(0)} km` : "not enough data"}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="h-72">
          {economy?.trend?.filter((t) => t.mileage != null).length ? (
            <ResponsiveContainer>
              <LineChart data={economy.trend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" stroke="#737373" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#737373" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", borderRadius: 0 }} />
                <Legend />
                <Line type="monotone" name="km/l" dataKey="mileage" stroke="#FF5A1F" strokeWidth={2} dot={{ r: 3, fill: "#FF5A1F" }} />
                <Line type="monotone" name="₹/km" dataKey="cost_per_km" stroke="#3B82F6" strokeWidth={2} dot={{ r: 2, fill: "#3B82F6" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-[#737373] text-sm">Need at least 2 refuels for this vehicle to compute trend.</div>
          )}
        </div>
      </div>

      {/* Station-wise spend */}
      <div className="ft-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="ft-overline">STATION-WISE SPEND</p>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Where you fill up most</h3>
          </div>
          <Gauge size={20} className="text-[#FF5A1F]" />
        </div>
        <div className="h-72">
          {summary?.station_spend?.length ? (
            <ResponsiveContainer>
              <BarChart data={summary.station_spend} layout="vertical" margin={{ top: 8, right: 24, left: 40, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" stroke="#737373" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v}`} />
                <YAxis type="category" dataKey="station" stroke="#737373" fontSize={11} tickLine={false} axisLine={false} width={120} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", borderRadius: 0 }} formatter={(v) => INR(v)} />
                <Bar dataKey="amount" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart />
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, danger }) {
  return (
    <div>
      <p className="ft-overline">{label}</p>
      <p className={`font-mono text-2xl font-medium mt-2 ${danger ? "text-[#EF4444]" : "text-white"}`}>{value}</p>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-full flex items-center justify-center text-[#737373] text-sm">
      No data yet.
    </div>
  );
}
